<script lang="ts" setup>
import { ref, toRef, PropType, computed, onBeforeUnmount } from 'vue'
import type { FeatherIconNames } from 'feather-icons'
import type { MdIconType } from '@/utils/icon.ts'
import { useFancyBackground, BackgroundType } from '@/composables/effects'
import Icon from '@/components/Icon.vue'
import MdIcon from '@/components/MdIcon.vue'
import { useWidgetStore } from '@/stores'

const props = defineProps({
  type: {
    type: String as PropType<BackgroundType>,
    default: 'default'
  },
  icon: {
    type: String as PropType<FeatherIconNames | MdIconType>,
    default: 'unknown'
  },
  size: {
    type: String as PropType<ButtonSize>,
    default: 'base'
  },
  title: {
    type: String,
    default: undefined
  },
  disabled: Boolean
})

const emits = defineEmits(['click'])
const widgetStore = useWidgetStore()

const isDisabled = toRef(props, 'disabled')

const buttonElement = ref<HTMLElement | null>(null)

const { backgroundStyle, elementClasses } = useFancyBackground(buttonElement, {
  isDisabled,
  type: props.type,
  onClick: () => emits('click')
})

const defaultClasses = [
  'rounded-full',
  'grid',
  'place-items-center',
  'select-none'
]

const outerClasses = computed(() => {
  const classes = [...defaultClasses, ...elementClasses.value]

  switch (props.size) {
    case 'sm':
      classes.push('w-6', 'h-6')
      break
    case 'base':
      classes.push('w-8', 'h-8')
      break
    case 'lg':
      classes.push('w-12', 'h-12')
      break
  }

  return classes
})

const isMdIcon = (iconName: string): iconName is MdIconType => {
  return iconName.startsWith('mdi')
}

let longPressTimeout: ReturnType<typeof setTimeout> | null = null
let startX = 0
let startY = 0
let isLongPressTriggered = false

const clearLongPress = () => {
  if (longPressTimeout) {
    clearTimeout(longPressTimeout)
    longPressTimeout = null
  }
}

const handlePointerDown = (event: PointerEvent) => {
  if (event.pointerType === 'mouse' && event.button !== 0) return

  const title = props.title

  if (!title) return

  clearLongPress()
  isLongPressTriggered = false
  startX = event.clientX
  startY = event.clientY

  longPressTimeout = setTimeout(() => {
    longPressTimeout = null
    isLongPressTriggered = true
    widgetStore.createSnackbar({
      text: title,
      actionText: '',
      variant: 'tooltip',
      duration: 2500
    })
    if (navigator.vibrate) {
      navigator.vibrate(50)
    }
  }, 500)
}

const handlePointerMove = (event: PointerEvent) => {
  if (!longPressTimeout) return

  const deltaX = Math.abs(event.clientX - startX)
  const deltaY = Math.abs(event.clientY - startY)
  // Threshold of 10px allows slight finger jitter while cancelling on scroll/drag
  if (deltaX > 10 || deltaY > 10) {
    clearLongPress()
  }
}

const handlePointerUp = () => {
  clearLongPress()

  if (isLongPressTriggered) {
    setTimeout(() => {
      isLongPressTriggered = false
    }, 400)
  }
}

const handlePointerCancel = () => {
  clearLongPress()
  isLongPressTriggered = false
}

const handlePointerLeave = (event: PointerEvent) => {
  if (event.pointerType === 'mouse') {
    clearLongPress()
  }
}

const handleContextMenu = (event: Event) => {
  if (props.title) {
    event.preventDefault()
  }
}

const handleClick = (event: MouseEvent) => {
  if (isLongPressTriggered) {
    event.preventDefault()
    event.stopPropagation()
    event.stopImmediatePropagation()
    isLongPressTriggered = false
  }
}

onBeforeUnmount(() => {
  clearLongPress()
})
</script>

<template>
  <button
    ref="buttonElement"
    :title="title"
    :class="outerClasses"
    style="-webkit-touch-callout: none"
    @pointerdown="handlePointerDown"
    @pointermove="handlePointerMove"
    @pointerup="handlePointerUp"
    @pointercancel="handlePointerCancel"
    @pointerleave="handlePointerLeave"
    @contextmenu="handleContextMenu"
    @click.capture="handleClick"
  >
    <span
      class="block absolute top-0 left-0 -z-10 w-full h-full"
      :style="{
        background: backgroundStyle
      }"
    />
    <MdIcon v-if="isMdIcon(icon)" :type="icon" :size="size" />
    <Icon v-else :type="icon" :size="size" />
  </button>
</template>
